<div class="space-y-8" [formGroup]="form()">
  <!-- Base rate -->
  <section class="space-y-5">
    <div class="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3.5">
      <div>
        <p class="text-xs text-slate-500">Derived from comparables</p>
        <p class="mt-1 text-2xl font-bold tabular-nums text-slate-900">{{ derivedRate() }}</p>
        <p class="mt-0.5 text-xs text-slate-500">{{ basisNote() }}</p>
      </div>

      <!-- A plot's base rate is the Land Rate from the Land Value step. -->
      @if (!isPlot()) {
        <label class="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700">
          <input
            type="checkbox"
            formControlName="overrideBase"
            (change)="onOverrideToggle()"
            class="size-4 rounded border-slate-300 accent-indigo-600"
          />
          Set rate manually
        </label>
      }
    </div>

    <div class="sm:max-w-sm">
      <ui-input
        [control]="form().controls.baseBenchmarkPsf"
        [label]="baseLabel()"
        type="number"
        prefix="₹"
        suffix="/sq.ft"
        [required]="true"
        [readonly]="isPlot()"
        [hint]="isPlot() ? 'Change the Land Rate in the Land Value step.' : ''"
      />
    </div>
  </section>

  <div class="h-px bg-slate-200"></div>

  <!-- Adjustment matrix from the API -->
  <section class="space-y-6" formGroupName="answers">
    @for (question of questions(); track question.key; let last = $last) {
      <fieldset
        class="space-y-3"
        [class.border-b]="!last"
        [class.border-slate-200]="!last"
        [class.pb-6]="!last"
      >
        <legend class="w-full">
          <span class="block text-sm font-bold text-slate-900">{{ question.label }}</span>
          @if (question.whyItMatters) {
            <span class="mt-0.5 block text-sm leading-relaxed text-slate-500">
              {{ question.whyItMatters }}
            </span>
          }
        </legend>

        <div class="grid gap-2">
          @for (option of question.options; track option.value) {
            <label
              class="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3
                     transition-colors has-[:checked]:border-indigo-400
                     has-[:checked]:bg-indigo-50/70 has-[:checked]:ring-1
                     has-[:checked]:ring-indigo-400"
              [class]="
                answerControl(question.key).value === option.value
                  ? ''
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              "
            >
              <input
                type="radio"
                [formControlName]="question.key"
                [value]="option.value"
                class="size-4 shrink-0 accent-indigo-600"
              />
              <span class="flex-1 text-sm leading-snug text-slate-900">{{ option.label }}</span>
              <span class="shrink-0 text-xs font-semibold tabular-nums" [class]="weightClass(option.weight)">
                {{ weight(option.weight) }}
              </span>
            </label>
          }
        </div>

        @if (showError(question.key)) {
          <p class="text-xs font-medium text-red-600" role="alert">
            Select one option for this factor.
          </p>
        }
      </fieldset>
    } @empty {
      <ui-note-bar tone="warn">
        No adjustment factors returned by the API. Seed the CI questions, then reload.
      </ui-note-bar>
    }
  </section>

  <!-- Running total -->
  <div class="grid divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
    <div class="px-5 py-4">
      <p class="text-xs text-slate-500">Base Rate</p>
      <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">
        {{ psf(form().controls.baseBenchmarkPsf.value) }}
      </p>
    </div>
    <div class="px-5 py-4">
      <p class="text-xs text-slate-500">Total Adjustment</p>
      <p class="mt-1 text-lg font-bold tabular-nums" [class]="totalAdjustmentClass()">
        {{ totalAdjustment() }}
      </p>
    </div>
    <div class="bg-indigo-50/70 px-5 py-4">
      <p class="text-xs text-indigo-600">Revised Benchmark</p>
      <p class="mt-1 text-lg font-bold tabular-nums text-indigo-700">{{ revisedRate() }}</p>
    </div>
  </div>

  <!-- Analyst note: generated by the calculate API -->
  <div class="space-y-2">
    <p class="block text-sm font-medium text-slate-700">Analyst Note</p>
    <p class="rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-[15px] leading-relaxed text-slate-700">
      {{ result()?.analystNotes || 'Generated automatically once the calculation completes.' }}
    </p>
  </div>

  @if (calculating() && !result()) {
    <p class="text-sm text-slate-500" role="status">Calculating…</p>
  }

  @if (matchingResult(); as r) {
    <div class="h-px bg-slate-200"></div>

    <!-- Pricing analysis -->
    <section class="space-y-4" [class.opacity-60]="calculating()" [attr.aria-busy]="calculating()">
      <h3 class="text-sm font-bold text-slate-900">Pricing Analysis</h3>
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Quoted PSF</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ psf(r.quotedPsf) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Benchmark PSF</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ psf(r.benchmarkPsf) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Ask vs Benchmark %</p>
          <p class="mt-1 text-lg font-bold tabular-nums" [class]="weightClass(r.askVsBenchmarkPercent)">
            {{ signedPct(r.askVsBenchmarkPercent * 100) }}
          </p>
        </div>
        <div class="rounded-lg border border-indigo-200 bg-indigo-50/70 px-5 py-4">
          <p class="text-xs text-indigo-600">Revised Benchmark</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-indigo-700">{{ revisedRate() }}</p>
        </div>
      </div>
    </section>

    <!-- Verdicts -->
    <section class="grid gap-4 sm:grid-cols-3">
      <div class="rounded-xl border border-slate-200 p-5">
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Initial CI Verdict</p>
        <p class="mt-1.5 text-xl font-bold text-slate-900">{{ r.initialVerdict }}</p>
        <p class="mt-1 text-xs leading-relaxed text-slate-500">
          Based on base benchmark, quoted PSF, UDS support and trophy override.
        </p>
      </div>
      <div class="rounded-xl border border-slate-200 p-5">
        <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Adjusted Verdict</p>
        <p class="mt-1.5 text-xl font-bold text-slate-900">{{ r.adjustedVerdict }}</p>
        <p class="mt-1 text-xs leading-relaxed text-slate-500">After the revised benchmark checklist.</p>
      </div>
      <div class="rounded-xl border border-indigo-200 bg-indigo-50/60 p-5">
        <p class="text-xs font-semibold uppercase tracking-wide text-indigo-600">Final CI Verdict</p>
        <p class="mt-1.5 text-xl font-bold text-indigo-900">{{ r.circledIntelligenceVerdict }}</p>
        <p class="mt-1 text-xs leading-relaxed text-indigo-700">{{ r.verdictDescription }}</p>
      </div>
    </section>

    <!-- Pricing & negotiation outputs -->
    <section class="space-y-4">
      <h3 class="text-sm font-bold text-slate-900">Pricing & Negotiation Outputs</h3>
      <div class="grid gap-4 sm:grid-cols-2">
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Suggested Offer PSF</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ psf(r.suggestedOfferPsf) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Close PSF Low</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ psf(r.closePsfLow) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Close PSF High</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ psf(r.closePsfHigh) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Suggested Offer</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">{{ compact(r.suggestedOfferValue) }}</p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Likely Close</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">
            {{ compact(r.likelyCloseValueLow) }} – {{ compact(r.likelyCloseValueHigh) }}
          </p>
        </div>
        <div class="rounded-lg border border-slate-200 bg-slate-50/60 px-5 py-4">
          <p class="text-xs text-slate-500">Fair Value Band</p>
          <p class="mt-1 text-lg font-bold tabular-nums text-slate-900">
            {{ compact(r.fairValueBandLow) }} – {{ compact(r.fairValueBandHigh) }}
          </p>
        </div>
      </div>
    </section>
  }
</div>
